/**
 * PaymentClient - Client-side API calls for deposit (Telegram Stars), withdraw,
 * balance, and transaction history.
 */

const API_BASE = import.meta.env.PROD
  ? `${window.location.origin}/api`
  : '/api'

export interface Transaction {
  id: string
  type: string
  amount: number
  gameId: string | null
  description: string | null
  createdAt: string
}

export interface TransactionPage {
  transactions: Transaction[]
  meta: { total: number; page: number; pages: number }
}

function authHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }
}

export const PaymentClient = {
  /**
   * Request a deposit invoice and open it via Telegram WebApp.openInvoice.
   * Returns true if user completed payment, false if cancelled/failed.
   */
  async deposit(amount: number, token: string): Promise<boolean> {
    const res = await fetch(`${API_BASE}/payments/deposit`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ amount }),
    })

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ error: 'Deposit request failed' }))
      throw new Error(errBody.error || 'Deposit request failed')
    }

    const data = await res.json()
    const invoiceUrl: string = data.data?.invoiceUrl ?? data.invoiceUrl

    if (!invoiceUrl) {
      throw new Error('No invoice URL received from server')
    }

    // Use Telegram WebApp.openInvoice if available (keeps user in Mini App)
    const tgWebApp = window.Telegram?.WebApp
    if (tgWebApp && 'openInvoice' in tgWebApp) {
      return new Promise<boolean>((resolve) => {
        const openInvoice = (tgWebApp as TelegramWebAppWithInvoice).openInvoice
        openInvoice(invoiceUrl, (status: string) => {
          if (status === 'paid') {
            // Brief delay to allow webhook processing on server
            setTimeout(() => resolve(true), 1500)
          } else {
            // 'cancelled', 'failed', 'pending'
            resolve(false)
          }
        })
      })
    }

    // Fallback: open invoice URL in new window
    window.open(invoiceUrl, '_blank')
    return false
  },

  /**
   * Request a payout (admin-approved withdrawal).
   * Returns the request ID.
   */
  async requestPayout(amount: number, token: string): Promise<{ requestId: string }> {
    const res = await fetch(`${API_BASE}/payments/payout-request`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ amount }),
    })

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ error: 'Payout request failed' }))
      throw new Error(errBody.error || 'Payout request failed')
    }

    const data = await res.json()
    return { requestId: data.data?.requestId ?? data.requestId }
  },

  /**
   * Check if user has a pending payout request.
   */
  async getPayoutStatus(token: string): Promise<{ pending: boolean; amount?: number }> {
    const res = await fetch(`${API_BASE}/payments/payout-status`, {
      method: 'GET',
      headers: authHeaders(token),
    })

    if (!res.ok) return { pending: false }

    const data = await res.json()
    const req = data.data?.request ?? data.request
    if (req) return { pending: true, amount: req.requestedStars }
    return { pending: false }
  },

  /**
   * Fetch current player balance from profile stats.
   */
  async getBalance(token: string): Promise<number> {
    const res = await fetch(`${API_BASE}/profile/stats`, {
      method: 'GET',
      headers: authHeaders(token),
    })

    if (!res.ok) {
      throw new Error('Failed to fetch balance')
    }

    const data = await res.json()
    return data.data?.balance ?? data.balance ?? 0
  },

  /**
   * Fetch paginated transaction history.
   */
  async getTransactions(token: string, page?: number): Promise<TransactionPage> {
    const params = new URLSearchParams()
    if (page !== undefined) params.set('page', String(page))

    const url = `${API_BASE}/profile/transactions${params.toString() ? '?' + params.toString() : ''}`
    const res = await fetch(url, {
      method: 'GET',
      headers: authHeaders(token),
    })

    if (!res.ok) {
      throw new Error('Failed to fetch transactions')
    }

    const data = await res.json()
    return {
      transactions: data.data?.transactions ?? data.transactions ?? [],
      meta: data.data?.meta ?? data.meta ?? { total: 0, page: 1, pages: 1 },
    }
  },
}

/**
 * Extended Telegram WebApp type with openInvoice method.
 * The base Window.Telegram.WebApp declaration in main.ts does not include
 * openInvoice, so we use a narrowed type here.
 */
interface TelegramWebAppWithInvoice {
  openInvoice: (url: string, callback: (status: string) => void) => void
}
